import * as React from "react";
import { IChartDataItemV2 } from "@iliotech/types";

import moment from "moment";
import { DRILL_DOWNS, getBackgroundColor } from "@iliotech/data-wire";
import { PortfolioSnapshotDtoV3 } from "@iliotech/generated-api-v3";
import { AllocationOption } from "@iliotech/data-wire/src/reactHooks/context/StructureGrouping/types";

type UseGetGroupedDataArgs = {
  grouping: AllocationOption;
  selectedGroup?: { code: string; label: string } | undefined;
  t(s: string): string;
};

export const useGetGroupedData = ({
  grouping,
  selectedGroup,
  t,
}: UseGetGroupedDataArgs) => {
  const getGroupedTableData = React.useCallback(
    (snapshotsToUse?: PortfolioSnapshotDtoV3[]) => {
      const filterField = grouping.field; // filter.field as keyof PortfolioSnapshotDTO;
      const filterId = selectedGroup?.code;

      const filteredSnapshots = filterId
        ? // @ts-ignore
          snapshotsToUse.filter((s) => s[filterField] === filterId)
        : snapshotsToUse;

      return filteredSnapshots || [];
    },
    [
      grouping?.field,
      grouping?.getColor,
      grouping?.labelField,
      selectedGroup?.code,
      t,
    ]
  );
  const getGroupedData = React.useCallback(
    (snapshotsToUse?: PortfolioSnapshotDtoV3[]) => {
      const groupedData: { [grouping: string]: IChartDataItemV2[] } = {};
      const filterField = grouping.field; // filter.field as keyof PortfolioSnapshotDTO;
      const filterLabel = grouping.labelField; // filter.field as keyof PortfolioSnapshotDTO;
      const filterId = selectedGroup?.code;
      const aggregateField = (
        !filterId
          ? filterField
          : DRILL_DOWNS[grouping.field]?.field || filterField
      ) as keyof PortfolioSnapshotDtoV3;

      const aggregateLabel = (
        !filterId
          ? filterLabel
          : DRILL_DOWNS[grouping.field]?.labelField || filterLabel
      ) as keyof PortfolioSnapshotDtoV3;

      const filteredSnapshots = filterId
        ? // @ts-ignore
          snapshotsToUse.filter((s) => s[filterField] === filterId)
        : snapshotsToUse;

      const getChartItemFromSnapshot = (snapshot: PortfolioSnapshotDtoV3) => {
        return {
          category: snapshot[aggregateLabel] as any,
          name: snapshot.name + " [" + snapshot.custodianName + "]",
          id: snapshot.instrumentId,
          color:
            grouping?.getColor!(snapshot[aggregateField] as any) ||
            getBackgroundColor((snapshot[aggregateField] as any) || "null"),
          value: snapshot.pnlBase,
          date: moment(snapshot.pricingDate).toDate(),
          tooltipExtra: [
            {
              label: t("Instrument name"),
              value: snapshot.name,
            },
            {
              label: t("Date"),
              value: moment(snapshot.pricingDate).format("YYYY-MM-DD"),
            },
          ],
        };
      };

      filteredSnapshots?.forEach((snapshot: PortfolioSnapshotDtoV3) => {
        groupedData[snapshot[aggregateField] as string] =
          groupedData[snapshot[aggregateField] as string] || [];

        groupedData[snapshot[aggregateField] as string].push(
          getChartItemFromSnapshot(snapshot) as any
        );
      });
      return groupedData;
    },
    [
      grouping?.field,
      grouping?.getColor,
      grouping?.labelField,
      selectedGroup?.code,
      t,
    ]
  );

  return {
    getGroupedData,
    getGroupedTableData,
  };
};
